import type { Navigator, Screen } from "../app/screen";
import { Api, ApiError } from "../net/api";
import { pageShell, note } from "../ui/page";
import { REALM_COLOUR } from "../card-frames.generated";
import { choice, setChoice } from "../app/session";
import { audio } from "../fx/audio";

/**
 * THE CAMPAIGN - five realms, thirty-five stages.
 *
 * 🚨 **THIS CONTENT WAS AUTHORED AND UNREACHABLE.** `Campaign/Progress` has returned five quadrants
 * of eight stages (three for The Ironworks) since the server was built - each with a title, a
 * subtitle, player-facing `ruleText`, `starRewardText`, an `arenaTheme`, a `botDeckId` and per-stage
 * `modifiers` - and no screen in the client could reach any of it. The only campaign surface was the
 * five-rung gauntlet. Alex found it by asking whether the campaigns get harder:
 *
 *     *"how am i expected to get lucky and ask"*
 *
 * ⭐ **THE DESIGN IS THE DATA'S, NOT MINE.** Every stage already specifies what Alex asked for:
 *
 *   · a LOOK per realm      `arenaTheme` = the quadrant
 *   · a PLAY STYLE per realm `botDeckId` differs per quadrant
 *   · a DIFFICULTY per stage `modifiers`: enemy +hp/+armor/+opening cards/+opening mana, against
 *                            your own smaller bonuses - and `ruleText` explains it in plain words
 *
 * The server already applies those modifiers (`Campaign/StartStage` → `GetStageModifiers`). So this
 * screen renders what exists rather than inventing a parallel campaign, which would be a second
 * owner of the whole design.
 *
 * ⚠️ **THE STAGE'S OWN RULE TEXT IS SHOWN, NEVER SUMMARISED.** Restating "+2 HP, +1 armor" here
 * would be a second owner of the handicap and would be wrong the first time one is balanced.
 */

interface Stage {
  stageIndex?: number;
  title?: string;
  subtitle?: string;
  ruleText?: string;
  starRewardText?: string;
  encounterType?: string;
  arenaTheme?: string;
  isHeroic?: boolean;
  /** A SPECIAL encounter's persistent rule - see `.claude/skills/revival-encounters/SKILL.md`. */
  specialRuleName?: string;
  specialRuleText?: string;
  isUnlocked?: boolean;
  isCompleted?: boolean;
  stars?: number;
}

interface Quadrant {
  id?: string;
  quadrant?: string;
  isUnlocked?: boolean;
  stages?: Stage[];
}

export const QuadrantsScreen: Screen = {
  id: "campaigns",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "THE FIVE REALMS", "thirty-five trials");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "realms";
    shell.body.append(root);
    root.append(note("Reading the realms..."));

    let data: { quadrants?: Quadrant[]; totalStars?: number };
    try {
      data = await Api.campaignProgress() as typeof data;
    } catch (e) {
      root.replaceChildren(note(e instanceof ApiError ? e.body || e.message : String(e), true));
      return;
    }

    const quadrants = (data.quadrants ?? []).filter((q) => (q.stages ?? []).length > 0);
    if (!quadrants.length) {
      root.replaceChildren(note("The campaign returned no realms.", true));
      return;
    }

    // The realm being viewed. Defaults to the first one that is open - never to a locked realm,
    // which would open the screen on a wall.
    let current = quadrants.find((q) => q.isUnlocked !== false) ?? quadrants[0]!;

    function realmId(q: Quadrant): string {
      return String(q.id ?? q.quadrant ?? "");
    }

    function draw(): void {
      root.replaceChildren();
      const id = realmId(current);
      const colour = REALM_COLOUR[id];
      if (colour) root.style.setProperty("--realm", colour);
      root.dataset["realm"] = id;

      // ── the realms ──────────────────────────────────────────────────────────────────────────
      const tabs = document.createElement("div");
      tabs.className = "realms__tabs";
      for (const q of quadrants) {
        const qid = realmId(q);
        const locked = q.isUnlocked === false;
        const b = document.createElement("button");
        b.type = "button";
        b.className = "realms__tab" + (q === current ? " realms__tab--on" : "")
          + (locked ? " realms__tab--locked" : "");
        b.dataset["label"] = qid.replace(/_/g, " ");
        b.textContent = qid.replace(/_/g, " ");
        if (REALM_COLOUR[qid]) b.style.setProperty("--realm", REALM_COLOUR[qid]!);
        // ⚠️ A locked realm is SHOWN and says why. Hiding it removes the reason to keep playing.
        b.title = locked ? "Clear the four core realms to open this one" : "";
        b.disabled = locked;
        b.addEventListener("click", () => { current = q; audio.play("click", 0.6); draw(); });
        tabs.append(b);
      }

      const stages = (current.stages ?? []).slice()
        .sort((a, b) => (a.stageIndex ?? 0) - (b.stageIndex ?? 0));

      const done = stages.filter((s) => s.isCompleted).length;
      const stars = stages.reduce((n, s) => n + (s.stars ?? 0), 0);
      const head = document.createElement("p");
      head.className = "realms__progress";
      head.textContent = `${done} of ${stages.length} cleared · ${stars} of ${stages.length * 3} stars`;

      const list = document.createElement("div");
      list.className = "realms__stages";

      for (const st of stages) {
        const locked = st.isUnlocked === false;
        const row = document.createElement("div");
        // ⭐ `data-stage` is the CONTENT-COVERAGE contract: `verify_content_coverage.py` counts these
        // against the 35 the server authored. A machine channel, never read for presentation.
        row.dataset["stage"] = `${realmId(current)}:${st.stageIndex ?? 0}`;
        row.className = "realms__stage"
          + (st.isCompleted ? " realms__stage--done" : "")
          + (locked ? " realms__stage--locked" : "")
          + (st.isHeroic ? " realms__stage--heroic" : "");

        const n = document.createElement("span");
        n.className = "realms__stageno";
        n.textContent = String(st.stageIndex ?? "");

        const body = document.createElement("div");
        body.className = "realms__stagebody";

        const t = document.createElement("span");
        t.className = "realms__stagetitle";
        t.textContent = String(st.title ?? "");
        if (st.isHeroic) t.append(tag("HEROIC"));
        if ((st.encounterType ?? "Regular") !== "Regular") {
          t.append(tag(String(st.encounterType)));
        }

        // ⭐ THE STAGE'S OWN WORDS. This is where the difficulty is explained, and it is the reason
        // a player picks one stage over another.
        const rule = document.createElement("span");
        rule.className = "realms__stagerule";
        rule.textContent = String(st.ruleText ?? "");

        body.append(t, rule);

        // ⭐ **A SPECIAL ENCOUNTER SAYS WHAT IT DOES, BEFORE YOU COMMIT.** Marvel Snap's Locations
        // are readable before you play into them and that is most of why they work: a rule you
        // discover mid-match is a surprise, and a surprise that changes the rules is a bad one.
        if (st.specialRuleText) {
          const sr = document.createElement("span");
          sr.className = "realms__special";
          const nm = document.createElement("strong");
          nm.className = "realms__specialname";
          nm.textContent = String(st.specialRuleName ?? "SPECIAL RULE");
          const tx = document.createElement("span");
          tx.textContent = " " + st.specialRuleText;
          sr.append(nm, tx);
          body.append(sr);
        }

        const starsEl = document.createElement("span");
        starsEl.className = "realms__stars";
        starsEl.setAttribute("aria-label", `${st.stars ?? 0} of 3 stars`);
        for (let i = 0; i < 3; i++) {
          const s = document.createElement("span");
          s.className = "realms__star" + (i < (st.stars ?? 0) ? " realms__star--lit" : "");
          s.textContent = "★";
          starsEl.append(s);
        }

        const go = document.createElement("button");
        go.type = "button";
        go.className = "utilbtn realms__play";
        go.dataset["label"] = locked ? "LOCKED" : st.isCompleted ? "REPLAY" : "PLAY";
        go.textContent = locked ? "LOCKED" : st.isCompleted ? "REPLAY" : "PLAY";
        go.disabled = locked;
        go.addEventListener("click", () => void start(st));

        row.append(n, body, starsEl, go);
        list.append(row);
      }

      // ⭐ THE WAY INTO THE SECOND CAMPAIGN MODE. It was served and reachable from nowhere; a screen
      // that exists but has no door is the same defect one step later.
      const more = document.createElement("button");
      more.type = "button";
      more.className = "utilbtn realms__hero";
      more.dataset["label"] = "HERO TRIALS";
      more.textContent = "HERO TRIALS - learn a class, win its cards";
      more.title = "Eight trials played with the hero's own deck. The same challenge for everyone, "
        + "and the cards you win are yours to keep.";
      more.addEventListener("click", () => { audio.play("click", 0.6); nav.go("herocampaign"); });

      // ⚠️ **NO MODE-SWITCHING DOORS HERE ANY MORE.** They were a patch: the CAMPAIGN plate
      // pointed straight at this screen, so the other two campaigns had nowhere to be reached from
      // and I hung buttons on the realm list. `screens/campaignHub.ts` is the chooser now, which is
      // the page that used to exist and that this screen replaced by accident.
      root.append(tabs, head, list);
    }

    function tag(text: string): HTMLElement {
      const s = document.createElement("span");
      s.className = "realms__tag";
      s.textContent = text;
      return s;
    }

    async function start(st: Stage): Promise<void> {
      const busy = note(`Entering ${st.title ?? "the trial"}...`);
      root.append(busy);
      try {
        // ⚠️ The chosen deck goes with it - the same silent `Decks[0]` fallback that made every
        // solo match play the same deck applies here too.
        const deckId = choice().deckId;
        if (!deckId) {
          const decks = await Api.decks() as Array<{ id?: string; name?: string }>;
          const first = decks[0];
          if (first?.id) setChoice({ deckId: first.id, deckName: first.name ?? "" });
        }
        await Api.campaignStartStage({
          quadrant: realmId(current),
          stageIndex: st.stageIndex ?? 1,
          isHeroic: st.isHeroic === true,
          ...(choice().deckId ? { deckId: choice().deckId as string } : {}),
        });
        audio.play("queue");
        nav.go("match");
      } catch (e) {
        busy.replaceWith(note(e instanceof ApiError ? e.body || e.message : String(e), true));
      }
    }

    draw();
  },
};
